import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Image as ImageIcon, Loader2, Sparkles, SwitchCamera, Timer, Type, X, Zap, ZapOff } from "lucide-react";
import { toast } from "sonner";
import { FileUpload } from "@/components/FileUpload";
import { nativeCameraPermission } from "@/lib/nativeCamera";
import { useBackHandler } from "@/lib/backButton";
import { useBodyScrollLock } from "@/lib/bodyScrollLock";
import { FX_PRESETS, DEFAULT_FX, fxFilterCss, fxToStore, applyFxToCanvas } from "@/lib/mediaFx";
import { BEAUTY_LEVELS, loadFaceLandmarker, drawSmoothFaces, makeScratch, readBeautyLevel, storeBeautyLevel, currentDelegate, switchDelegate } from "@/lib/faceSmooth";

// Lot 535 — Caméra intégrée (stories, publications, reels — téléphone / app native) : PHOTO · 15 s · 60 s · TEXTE, caméra
// avant/arrière, flash (torche ou éclair d'écran en selfie), minuteur 3 s / 10 s, filtres en direct (mêmes presets que
// l'éditeur média). Photo → fichier JPEG déjà filtré (+ miroir en selfie) ; vidéo → fichier brut + fx (CSS à la lecture)
// + durée mesurée (un WebM MediaRecorder annonce une durée infinie). `onText` absent → pas de mode TEXTE ;
// `allowPhoto` false → vidéo seulement (reels).
// Lot 536 — « Beauté » : lissage visage MediaPipe sur l'aperçu (canvas) et sur la photo capturée (pleine résolution) ;
// 4 niveaux mémorisés. Lot 536b — vidéo : le canvas lissé (720p) est enregistré (captureStream + micro) ; si le téléphone
// ne suit plus (< 15 i/s pendant 2 s) le lissage est coupé pour garder la vidéo fluide (message).
const TIMERS = [0, 3, 10];
const PREVIEW_W = 540;
const RECORD_W = 720;
const MIN_FPS = 15;
// Android WebView dessine son « poster » par défaut (gros bouton ▶ gris) sur tout <video> sans image tant que le flux
// n'est pas attaché (pendant la demande d'autorisation) : un poster transparent l'écarte, comme pour le KYC.
const TRANSPARENT_POSTER = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const SLOW_GPU_MS = 90;
const round = "h-11 w-11 grid place-items-center rounded-full bg-black/45 text-white backdrop-blur-md active:scale-95 transition-transform";
const pickMime = () => ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const StoryCamera = ({ open, maxSec = 30, allowPhoto = true, initialMode = "photo", onCapture, onFiles, onText = null, onClose, t }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const canvasRef = useRef(null);
  const landmarkerRef = useRef(null);
  const facesRef = useRef([]);
  const scratchRef = useRef(null);
  const fpsRef = useRef(60);
  const perfRef = useRef({ det: 0, draw: 0 });
  const switchingRef = useRef(false);
  const degradedRef = useRef(false);
  const [state, setState] = useState("starting"); // starting | ready | denied | unavailable | error
  const [fit, setFit] = useState("cover"); // cover (flux portrait → plein écran comme TikTok) | contain (flux paysage inévitable)
  const [facing, setFacing] = useState("user");
  const [canSwitch, setCanSwitch] = useState(false);
  const [torchOk, setTorchOk] = useState(false);
  const [flash, setFlash] = useState(false);
  const [timer, setTimer] = useState(0);
  const [mode, setMode] = useState(allowPhoto && initialMode === "photo" ? "photo" : initialMode === "photo" ? "v15" : initialMode); // photo | v15 | v60
  const [preset, setPreset] = useState("normal");
  const [thumb, setThumb] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [screenFlash, setScreenFlash] = useState(false);
  const [beauty, setBeauty] = useState(readBeautyLevel);
  const [beautyState, setBeautyState] = useState("idle"); // idle | loading | ready | error
  const [beautyMenu, setBeautyMenu] = useState(false);
  const [faceSeen, setFaceSeen] = useState(true);
  const [degraded, setDegraded] = useState(false);
  const [fps, setFps] = useState(0); // diagnostic (data-fps), rafraîchi chaque seconde
  const canRecord = typeof window !== "undefined" && !!window.MediaRecorder;
  const videoModes = [{ key: "v15", sec: Math.min(15, maxSec) }, { key: "v60", sec: Math.min(60, maxSec) }].filter((m, i, arr) => canRecord && (i === 0 || m.sec !== arr[0].sec));
  const cap = videoModes.find((m) => m.key === mode)?.sec || 0;
  const fx = { ...DEFAULT_FX, preset };
  const mirror = facing === "user";
  const beautyOn = open && beauty > 0 && state === "ready" && beautyState === "ready";

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const grabThumb = () => {
    const v = videoRef.current;
    if (!v || v.readyState < 2 || !v.videoWidth) return;
    try {
      const c = document.createElement("canvas"); c.width = 72; c.height = Math.round(72 * v.videoHeight / v.videoWidth);
      const ctx = c.getContext("2d");
      if (mirror) { ctx.translate(c.width, 0); ctx.scale(-1, 1); }
      ctx.drawImage(v, 0, 0, c.width, c.height);
      setThumb(c.toDataURL("image/jpeg", 0.7));
    } catch { /* flux non lisible (CORS) → vignettes unies */ }
  };

  const start = useCallback(async () => {
    stop();
    setState("starting"); setTorchOk(false);
    if (!navigator.mediaDevices?.getUserMedia) { setState("unavailable"); return; }
    if ((await nativeCameraPermission()) === "denied") { setState("denied"); return; }
    // Lot 542 — les formats caméra Android sont énumérés en orientation CAPTEUR (paysage). Demander un idéal portrait
    // (1080×1920) faisait sortir, sur certains appareils, une bande 16:9 PAYSAGE recadrée (champ vertical perdu : zoom ×4
    // en cover, bande au milieu en contain). Comme le KYC : on demande 1920×1080 et le navigateur pivote selon
    // l'orientation ; si le flux arrive quand même en paysage sur un écran portrait, on retente au format natif.
    const base = { facingMode: { ideal: facing } };
    const attempts = [{ ...base, width: { ideal: 1920 }, height: { ideal: 1080 } }, base];
    const acquire = async (video) => {
      try { return await navigator.mediaDevices.getUserMedia({ video, audio: true }); }
      catch { return navigator.mediaDevices.getUserMedia({ video, audio: false }); }
    };
    const v = videoRef.current;
    const attach = async (s) => {
      if (!v) return [0, 0];
      v.srcObject = s; await v.play().catch(() => {});
      if (!v.videoWidth) await new Promise((res) => { const done = () => res(); v.addEventListener("loadedmetadata", done, { once: true }); setTimeout(done, 1500); });
      return [v.videoWidth, v.videoHeight];
    };
    const portraitScreen = window.innerHeight >= window.innerWidth;
    let stream;
    try {
      for (let i = 0; i < attempts.length; i++) {
        const s = await acquire(attempts[i]);
        const [w, h] = await attach(s);
        const landscape = w > h;
        if (i === attempts.length - 1 || !portraitScreen || !landscape) { stream = s; setFit(portraitScreen && landscape ? "contain" : "cover"); break; }
        s.getTracks().forEach((tr) => tr.stop());
      }
    } catch (e) { const n = e?.name || ""; setState(["NotAllowedError", "SecurityError", "PermissionDeniedError"].includes(n) ? "denied" : ["NotFoundError", "OverconstrainedError", "DevicesNotFoundError"].includes(n) ? "unavailable" : "error"); return; }
    streamRef.current = stream;
    try { setTorchOk(!!stream.getVideoTracks()[0]?.getCapabilities?.()?.torch); } catch { /* noop */ }
    try { const devs = await navigator.mediaDevices.enumerateDevices(); setCanSwitch(devs.filter((d) => d.kind === "videoinput").length > 1); } catch { /* noop */ }
    setState("ready");
    setTimeout(grabThumb, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing, stop]);

  useEffect(() => { if (open) start(); else stop(); return stop; }, [open, start, stop]);
  useEffect(() => {
    if (!open) return undefined;
    const onVis = () => { if (document.visibilityState === "hidden") { stopRec(); stop(); } else start(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, start, stop]);
  useBodyScrollLock(open);
  // Bouton Retour Android : ferme la caméra (pas l'éditeur derrière).
  useBackHandler(open, () => { stopRec(); onClose(); return true; });

  // Beauté : moteur chargé à la demande (1re activation), niveau mémorisé sur l'appareil.
  useEffect(() => {
    if (!open || !beauty || landmarkerRef.current || beautyState === "loading") return;
    let alive = true;
    setBeautyState("loading");
    loadFaceLandmarker()
      .then((lm) => { if (!alive) return; landmarkerRef.current = lm; setBeautyState("ready"); })
      .catch(() => { if (!alive) return; setBeautyState("error"); setBeauty(0); toast.error(t("camera.beauty_error", "Lissage indisponible sur cet appareil.")); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, beauty]);
  const pickBeauty = (n) => { setBeauty(n); storeBeautyLevel(n); setBeautyMenu(false); if (!n) facesRef.current = []; };

  // Aperçu lissé : canvas redessiné à chaque frame (détection + flou masqué) par-dessus la vidéo — 540 de large en
  // PHOTO, 720 en vidéo (c'est lui qui est enregistré). Fluidité surveillée : pendant un enregistrement, < 15 i/s pendant
  // 2 s → le lissage est coupé (le canvas ne fait plus que recopier la vidéo) et un message l'indique.
  useEffect(() => {
    if (!beautyOn) return undefined;
    let alive = true, raf = 0, lastTs = 0, missing = 0, lowSince = 0, lastFpsAt = 0;
    const times = [];
    const perf = { det: 0, draw: 0, n: 0 };
    if (!scratchRef.current) scratchRef.current = makeScratch();
    const loop = () => {
      if (!alive) return;
      const v = videoRef.current, c = canvasRef.current, lm = landmarkerRef.current;
      if (v && c && lm && v.readyState >= 2 && v.videoWidth) {
        const scale = Math.min(1, (mode === "photo" ? PREVIEW_W : RECORD_W) / v.videoWidth);
        const w = Math.round(v.videoWidth * scale), h = Math.round(v.videoHeight * scale);
        if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
        const now = performance.now();
        times.push(now); while (times.length && times[0] < now - 1000) times.shift();
        fpsRef.current = times.length;
        if (now - lastFpsAt > 1000) {
          lastFpsAt = now; setFps(times.length);
          perfRef.current = { det: Math.round(perf.det / Math.max(1, perf.n)), draw: Math.round(perf.draw / Math.max(1, perf.n)) };
          // GPU (WebGL) plus lent que ~90 ms par détection (WebGL logiciel) → on rebascule le moteur sur le CPU, une fois.
          // (≥ 2 mesures : à 400 ms/détection on n'a que 2-3 images/s, l'ancien seuil « ≥ 4 » ne basculait jamais.)
          if (perf.n >= 2 && perfRef.current.det > SLOW_GPU_MS && currentDelegate() === "GPU" && !switchingRef.current) {
            switchingRef.current = true;
            switchDelegate("CPU").catch(() => {}).finally(() => { switchingRef.current = false; });
          }
          perf.det = 0; perf.draw = 0; perf.n = 0;
        }
        const rec = recRef.current;
        if (rec && !degradedRef.current && now - rec._p0 > 1500) {
          if (times.length < MIN_FPS) { if (!lowSince) lowSince = now; else if (now - lowSince > 2000) { degradedRef.current = true; setDegraded(true); } }
          else lowSince = 0;
        }
        const ctx = c.getContext("2d");
        ctx.drawImage(v, 0, 0, w, h);
        if (!degradedRef.current) {
          if (now > lastTs && !switchingRef.current) {
            lastTs = now;
            const t1 = performance.now();
            try { facesRef.current = lm.detectForVideo(v, now).faceLandmarks || []; } catch { facesRef.current = []; }
            perf.det += performance.now() - t1;
            missing = facesRef.current.length ? 0 : missing + 1;
            if (missing === 20) setFaceSeen(false); else if (missing === 0) setFaceSeen(true);
          }
          const t2 = performance.now();
          drawSmoothFaces(ctx, facesRef.current, w, h, beauty, scratchRef.current);
          perf.draw += performance.now() - t2; perf.n += 1;
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [beautyOn, beauty, mode]);

  const torch = async (on) => {
    const tr = streamRef.current?.getVideoTracks()[0];
    if (!tr || !torchOk) return;
    try { await tr.applyConstraints({ advanced: [{ torch: on }] }); } catch { /* noop */ }
  };

  // Minuteur : décompte visible puis action.
  const withTimer = (fn) => {
    if (!timer) { fn(); return; }
    let n = timer;
    setCountdown(n);
    const id = setInterval(() => { n -= 1; if (n <= 0) { clearInterval(id); setCountdown(0); fn(); } else setCountdown(n); }, 1000);
  };

  const snap = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    if (flash) {
      if (mirror || !torchOk) { setScreenFlash(true); await new Promise((r) => setTimeout(r, 220)); }
      else { await torch(true); await new Promise((r) => setTimeout(r, 350)); }
    }
    const w = v.videoWidth, h = v.videoHeight;
    // Beauté : lissage appliqué d'abord (repère vidéo brut, landmarks normalisés du dernier frame), miroir et filtre ensuite.
    let src = v;
    if (beautyOn && facesRef.current.length) {
      const work = document.createElement("canvas"); work.width = w; work.height = h;
      const wctx = work.getContext("2d");
      wctx.drawImage(v, 0, 0, w, h);
      drawSmoothFaces(wctx, facesRef.current, w, h, beauty);
      src = work;
    }
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    if (mirror) { ctx.translate(w, 0); ctx.scale(-1, 1); }
    ctx.drawImage(src, 0, 0, w, h);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    applyFxToCanvas(ctx, w, h, fx);
    if (flash) { setScreenFlash(false); torch(false); }
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.92));
    if (blob) onCapture({ file: new File([blob], `camera-${Date.now()}.jpg`, { type: "image/jpeg" }), kind: "image", fx: null, width: w, height: h });
  };

  const startRec = () => {
    const stream = streamRef.current;
    if (!stream || recRef.current) return;
    const mime = pickMime();
    const v = videoRef.current;
    // Beauté active : on enregistre le canvas lissé (720p) + le micro ; téléphone déjà à la peine → vidéo brute d'emblée.
    let source = stream, canvasTracks = [];
    let dims = { width: v?.videoWidth || 0, height: v?.videoHeight || 0 };
    degradedRef.current = false; setDegraded(false);
    if (beautyOn && canvasRef.current?.captureStream) {
      if (fpsRef.current < MIN_FPS) { degradedRef.current = true; setDegraded(true); }
      try {
        const cs = canvasRef.current.captureStream(30);
        canvasTracks = cs.getVideoTracks();
        stream.getAudioTracks().forEach((tr) => cs.addTrack(tr));
        source = cs;
        dims = { width: canvasRef.current.width, height: canvasRef.current.height };
      } catch { source = stream; canvasTracks = []; }
    }
    let rec;
    try { rec = new MediaRecorder(source, mime ? { mimeType: mime } : undefined); } catch { try { rec = new MediaRecorder(source); } catch { return; } }
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      recRef.current = null;
      torch(false);
      canvasTracks.forEach((tr) => tr.stop());
      const type = rec.mimeType || mime || "video/webm";
      const blob = new Blob(chunks, { type });
      const duration = Math.round(((Date.now() - rec._t0) / 1000) * 100) / 100;
      setRecording(false); setElapsed(0);
      degradedRef.current = false; setDegraded(false);
      if (blob.size) onCapture({ file: new File([blob], `camera-${Date.now()}.${type.includes("mp4") ? "mp4" : "webm"}`, { type }), kind: "video", fx: fxToStore({ ...fx, flip: mirror }), duration, ...dims });
    };
    recRef.current = rec;
    rec.start(250);
    if (flash) torch(true);
    setRecording(true); setElapsed(0);
    const t0 = Date.now();
    rec._t0 = t0;
    rec._p0 = performance.now();
    const id = setInterval(() => {
      const s = (Date.now() - t0) / 1000;
      setElapsed(s);
      if (s >= cap) { clearInterval(id); stopRec(); }
    }, 100);
    rec._tick = id;
  };
  const stopRec = () => {
    const rec = recRef.current;
    if (!rec) return;
    clearInterval(rec._tick);
    try { if (rec.state !== "inactive") rec.stop(); } catch { recRef.current = null; setRecording(false); }
  };
  const onShutter = () => {
    if (state !== "ready" || countdown) return;
    if (mode === "photo") { withTimer(snap); return; }
    if (recording) stopRec(); else withTimer(startRec);
  };

  if (!open) return null;
  const previewStyle = { filter: fxFilterCss(fx), transform: mirror ? "scaleX(-1)" : undefined };
  const fitClass = fit === "contain" ? "object-contain" : "object-cover";
  const ring = recording && cap ? Math.min(100, (elapsed / cap) * 100) : 0;
  return createPortal(
    <div className="fixed inset-0 z-[95] bg-black text-white select-none overflow-hidden" style={{ pointerEvents: "auto" }} data-testid="story-camera" data-state={state} data-mode={mode} data-facing={facing} data-recording={recording ? 1 : 0} data-beauty={beauty} data-beauty-state={beautyState} data-beauty-on={beautyOn ? 1 : 0} data-beauty-degraded={degraded ? 1 : 0} data-fps={beautyOn ? fps : undefined} data-perf={beautyOn ? `det:${perfRef.current.det},draw:${perfRef.current.draw},${currentDelegate()}` : undefined}>
      <video ref={videoRef} autoPlay muted playsInline poster={TRANSPARENT_POSTER} className={`absolute inset-0 w-full h-full ${fitClass} transition-opacity duration-300 ${state === "ready" ? "opacity-100" : "opacity-0"}`} style={previewStyle} data-testid="story-camera-video" data-fit={fit} />
      <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full ${fitClass}`} style={{ ...previewStyle, display: beautyOn ? undefined : "none" }} data-testid="story-camera-beauty-canvas" data-faces={faceSeen ? 1 : 0} />
      {beautyOn && (degraded || !faceSeen) && (
        <div className="absolute inset-x-0 z-[7] flex justify-center pointer-events-none" style={{ top: "calc(var(--sa-top, 0px) + 64px)" }}>
          {degraded
            ? <span className="px-3 py-1 rounded-full bg-black/55 text-xs font-semibold" data-testid="story-camera-beauty-degraded">{t("camera.beauty_degraded", "Lissage coupé pour garder la vidéo fluide")}</span>
            : <span className="px-3 py-1 rounded-full bg-black/55 text-xs font-semibold" data-testid="story-camera-beauty-noface">{t("camera.beauty_noface", "Aucun visage détecté")}</span>}
        </div>
      )}
      {screenFlash && <div className="absolute inset-0 bg-white z-[5]" data-testid="story-camera-screen-flash" />}

      {state !== "ready" && (
        <div className="absolute inset-0 grid place-items-center px-8 text-center z-[4]" data-testid="story-camera-state">
          <div className="space-y-3">
            <Camera size={36} className="mx-auto opacity-80" />
            <p className="text-sm font-semibold">
              {state === "starting" ? t("camera.starting", "Ouverture de la caméra…")
                : state === "denied" ? t("camera.denied", "Autorise la caméra dans les réglages de ton téléphone pour filmer ici.")
                  : state === "unavailable" ? t("camera.unavailable", "Aucune caméra disponible sur cet appareil.")
                    : t("camera.error", "La caméra n'a pas pu démarrer.")}
            </p>
            {state !== "starting" && (
              <div className="flex items-center justify-center gap-2">
                <button type="button" onClick={start} className="h-10 px-4 rounded-full bg-white/15 text-sm font-semibold" data-testid="story-camera-retry">{t("common.retry", "Réessayer")}</button>
                <FileUpload accept="image/*,video/*" onFiles={onFiles} variant="custom" testId="story-camera-gallery-fallback" context="story" allowLargeVideo className="h-10 px-4 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 bg-[color:var(--yalola-primary)]">
                  <ImageIcon size={15} /> {t("story.tool_gallery", "Galerie")}
                </FileUpload>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Haut : fermer · (durée) · colonne d'outils à droite */}
      <div className="absolute inset-x-0 top-0 z-10 px-3 flex items-start justify-between" style={{ paddingTop: "calc(var(--sa-top, 0px) + 10px)" }}>
        <button type="button" onClick={() => { stopRec(); onClose(); }} aria-label={t("common.close", "Fermer")} className={round} data-testid="story-camera-close"><X size={20} /></button>
        {recording && <span className="mt-2 px-3 py-1 rounded-full bg-red-600 text-xs font-bold tabular-nums" data-testid="story-camera-elapsed">● {fmt(elapsed)} / {fmt(cap)}</span>}
        <div className="flex flex-col items-center gap-3">
          {canSwitch && <button type="button" disabled={recording} onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))} aria-label={t("camera.switch", "Retourner")} className={`${round} disabled:opacity-40`} data-testid="story-camera-switch"><SwitchCamera size={20} /></button>}
          <button type="button" onClick={() => setFlash((f) => !f)} aria-pressed={flash} aria-label={t("camera.flash", "Flash")} className={round} style={flash ? { background: "#fff", color: "#111" } : undefined} data-testid="story-camera-flash">{flash ? <Zap size={20} /> : <ZapOff size={20} />}</button>
          <button type="button" onClick={() => setTimer((x) => TIMERS[(TIMERS.indexOf(x) + 1) % TIMERS.length])} aria-label={t("camera.timer", "Minuteur")} className={`${round} relative`} style={timer ? { background: "#fff", color: "#111" } : undefined} data-testid="story-camera-timer" data-timer={timer}>
            <Timer size={20} />
            {timer > 0 && <span className="absolute -bottom-1 -right-1 h-5 min-w-5 px-1 rounded-full text-[10px] font-bold grid place-items-center text-white" style={{ background: "var(--yalola-primary)" }}>{timer}s</span>}
          </button>
          {!recording && (
            <div className="relative flex flex-col items-end">
              <button type="button" onClick={() => setBeautyMenu((o) => !o)} aria-label={t("camera.beauty", "Beauté")} aria-expanded={beautyMenu} className={`${round} relative`} style={beauty ? { background: "#fff", color: "#111" } : undefined} data-testid="story-camera-beauty" data-level={beauty}>
                {beautyState === "loading" ? <Loader2 size={20} className="animate-spin" /> : <Sparkles size={20} />}
                {beauty > 0 && <span className="absolute -bottom-1 -right-1 h-5 min-w-5 px-1 rounded-full text-[10px] font-bold grid place-items-center text-white" style={{ background: "var(--yalola-primary)" }}>{beauty}</span>}
              </button>
              {beautyMenu && (
                <div className="absolute right-12 top-0 flex items-center gap-1 p-1 rounded-full bg-black/60 backdrop-blur-md" role="radiogroup" data-testid="story-camera-beauty-menu">
                  {BEAUTY_LEVELS.map((l) => {
                    const on = beauty === l.level;
                    return (
                      <button key={l.level} type="button" role="radio" aria-checked={on} onClick={() => pickBeauty(l.level)} className={`h-9 px-3 rounded-full text-xs font-semibold whitespace-nowrap ${on ? "bg-white text-black" : "text-white/85"}`} data-testid={`story-camera-beauty-${l.level}`}>
                        {t(`camera.beauty_${l.key}`, { off: "Sans", light: "Léger", medium: "Moyen", strong: "Fort" }[l.key])}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {countdown > 0 && (
        <div className="absolute inset-0 z-[6] grid place-items-center pointer-events-none" data-testid="story-camera-countdown">
          <span className="text-[120px] font-black leading-none" style={{ textShadow: "0 4px 24px rgba(0,0,0,.6)" }}>{countdown}</span>
        </div>
      )}

      {/* Bas : filtres en direct · galerie / déclencheur · modes */}
      <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/80 via-black/35 to-transparent pt-10" style={{ paddingBottom: "calc(var(--sa-bottom, 0px) + 10px)" }}>
        {!recording && (
          <div className="flex gap-3 overflow-x-auto hide-scrollbar px-4 pb-3" data-testid="story-camera-filters">
            {FX_PRESETS.map((p) => {
              const on = preset === p.key;
              return (
                <button key={p.key} type="button" onClick={() => setPreset(p.key)} className="shrink-0 flex flex-col items-center gap-1 w-[56px]" data-testid={`story-camera-filter-${p.key}`} data-on={on ? 1 : 0}>
                  <span className="h-12 w-12 rounded-full overflow-hidden border-2 bg-white/20" style={{ borderColor: on ? "var(--yalola-primary)" : "rgba(255,255,255,.45)", transform: on ? "scale(1.1)" : "none" }}>
                    {thumb && <img src={thumb} alt="" className="h-full w-full object-cover" style={{ filter: fxFilterCss({ ...DEFAULT_FX, preset: p.key }) }} draggable={false} />}
                  </span>
                  <span className={`text-[10px] leading-none truncate w-full text-center ${on ? "font-bold" : "text-white/75"}`}>{t(`fx.preset_${p.key}`, p.label)}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="grid grid-cols-3 items-center px-6 pb-3">
          <div className="justify-self-start">
            {!recording && (
              <FileUpload accept="image/*,video/*" onFiles={onFiles} variant="custom" testId="story-camera-gallery" context="story" allowLargeVideo className="h-11 w-11 rounded-xl border-2 border-white/70 bg-black/40 grid place-items-center">
                <ImageIcon size={20} />
              </FileUpload>
            )}
          </div>
          <button type="button" onClick={onShutter} disabled={state !== "ready" || countdown > 0} aria-label={mode === "photo" ? t("camera.shoot", "Prendre la photo") : recording ? t("camera.stop", "Arrêter") : t("camera.record", "Filmer")} className="justify-self-center relative h-[76px] w-[76px] rounded-full grid place-items-center disabled:opacity-40" data-testid="story-camera-shutter">
            <svg viewBox="0 0 76 76" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden>
              <circle cx="38" cy="38" r="35" fill="none" stroke="rgba(255,255,255,.9)" strokeWidth="4" />
              {ring > 0 && <circle cx="38" cy="38" r="35" fill="none" stroke="#ef4444" strokeWidth="4" strokeDasharray={`${(ring / 100) * 219.9} 219.9`} strokeLinecap="round" />}
            </svg>
            <span className={`block rounded-full transition-all duration-200 ${recording ? "h-7 w-7 rounded-md bg-red-500" : mode === "photo" ? "h-[60px] w-[60px] bg-white" : "h-[60px] w-[60px] bg-red-500"}`} />
          </button>
          <div className="justify-self-end" />
        </div>
        {!recording && (
          <div className="flex items-center justify-center gap-5 px-4 pb-1" role="tablist" data-testid="story-camera-modes">
            {[...(allowPhoto ? [{ key: "photo", label: t("camera.mode_photo", "PHOTO") }] : []), ...videoModes.map((m) => ({ key: m.key, label: `${m.sec} s` })), ...(onText ? [{ key: "text", label: t("camera.mode_text", "TEXTE") }] : [])].map((m) => {
              const on = mode === m.key;
              return (
                <button key={m.key} type="button" role="tab" aria-selected={on} onClick={() => { setBeautyMenu(false); if (m.key === "text") onText(); else setMode(m.key); }} className={`text-xs tracking-wide py-1 ${on ? "font-extrabold text-white" : "font-semibold text-white/60"}`} data-testid={`story-camera-mode-${m.key}`}>
                  {m.key === "text" ? <span className="inline-flex items-center gap-1"><Type size={12} /> {m.label}</span> : m.label}
                  {on && <span className="block mx-auto mt-0.5 h-1 w-1 rounded-full bg-white" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default StoryCamera;
